import { useMemo } from 'react'
import { DateTime } from 'luxon'

import {
  matchFilterBetweenDates, matchFilterNumber, sortArrayBy,
} from '@domain-group/javascript-utils'
import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import formatDate from '@functions/formatDate'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { PublicationEditionModel, PublicationEditionRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['publicationEditions']

type PublicationEditionFilters = {
  max_publication_date?: string,
  min_publication_date?: string,
  publication_id?: number,
}

type UsePublicationEditionsOptions = {
  filters?: PublicationEditionFilters,
  performHttpRequests?: boolean,
  requestOptions?: PublicationEditionRequestOptions,
}

function usePublicationEditions(options: UsePublicationEditionsOptions) {
  const { filters = {}, requestOptions } = options
  const {
    max_publication_date: filterMaxPublicationDate,
    min_publication_date: filterMinPublicationDate,
    publication_id: filterPublicationId,
  } = filters

  const {
    updatedEntities: {
      publicationEditions: publicationEditionsUpdatedAt,
    },
  } = useWatchEntityUpdates(watchEntityKeys)

  const publicationEditions = useSelector(reduxState => reduxState.entities.publicationEditions)

  const filteredPublicationEditions = useMemo(() => {
    const filtered = Object.values(publicationEditions).filter((publicationEdition: PublicationEditionModel) => {
      const { publication_date, publication_id } = publicationEdition

      const formattedPublicationDate = formatDate(publication_date, 'dd/MM/yyyy')

      const matchPublication = matchFilterNumber(publication_id, filterPublicationId)
      const matchPublicationDate = matchFilterBetweenDates(
        formattedPublicationDate,
        filterMinPublicationDate,
        filterMaxPublicationDate,
      )

      return matchPublication && matchPublicationDate
    })

    return sortArrayBy(filtered, 'asc', edition => DateTime.fromFormat(edition.publication_date, 'dd/MM/yyyy'))
  }, [publicationEditionsUpdatedAt, JSON.stringify(filters)])

  const filteredPublicationEditionsCount = filteredPublicationEditions.length
  const hasFilteredPublicationEditions = !!filteredPublicationEditionsCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredPublicationEditionsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingPublicationEditions } = useReduxAction(
    'publicationEditions',
    'loadPublicationEditions',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredPublicationEditions,
    filteredPublicationEditionsCount,
    hasFilteredPublicationEditions,
    loading: loadingPublicationEditions,
  }
}

export default usePublicationEditions
